import '../styles/SignUpEnrollmentPanel.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const RESEARCHERS_COLLECTION = 'researchers';
const CRAWLS_PATH = '/crawls';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values) {
  const errors = {};
  const email = values.email.trim();

  if (!email) {
    errors.email = 'Enter the email address you want to enroll with.';
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = 'Enter a complete email address, like name@example.com.';
  }

  if (!values.password) {
    errors.password = 'Choose a password.';
  } else if (values.password.length < 8) {
    errors.password = 'Use at least 8 characters.';
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Re-enter your password to confirm it.';
  } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = 'The two passwords do not match.';
  }

  return errors;
}

function DrawnMark() {
  return (
    <span className="sep-mark" aria-hidden="true">
      <svg viewBox="0 0 40 22" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M2 16 C10 6, 22 4, 32 7" />
        <path d="M8 19 C16 11, 26 9, 37 11" opacity="0.6" />
        <circle cx="33" cy="7" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    </span>
  );
}

function SubmitArrow() {
  return (
    <span className="sep-submit-arrow" aria-hidden="true">
      <svg viewBox="0 0 22 10" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M0 5 H20" />
        <path d="M16 1 L20 5 L16 9" />
      </svg>
    </span>
  );
}

function Field({ id, label, hint, type, value, error, onChange, onBlur, autoComplete, disabled }) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="sep-field">
      <div className="sep-field-row">
        <label className="sep-field-label" htmlFor={id}>{label}</label>
        {hint ? <span className="sep-field-hint" id={hintId}>{hint}</span> : null}
      </div>
      <input
        id={id}
        className="sep-input"
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={describedBy}
        disabled={disabled}
        required
      />
      {error ? <p className="sep-field-error" id={errorId}>{error}</p> : null}
    </div>
  );
}

function SignUpEnrollmentPanel({ onSubmit }) {
  const { data, loading, error: loadError, refresh } = useProjectCollection(RESEARCHERS_COLLECTION);
  const [values, setValues] = useState({ email: '', password: '', confirmPassword: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [createdEmail, setCreatedEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const researchers = Array.isArray(data) ? data : [];
  const lookupFailed = Boolean(loadError);

  function update(fieldName, nextValue) {
    setValues((prev) => ({ ...prev, [fieldName]: nextValue }));
    setFieldErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev));
    setFormError('');
    setCreatedEmail('');
  }

  function blur(fieldName) {
    const errors = validate(values);
    if (errors[fieldName]) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: errors[fieldName] }));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    setFormError('');
    setCreatedEmail('');
    const errors = validate(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (lookupFailed) {
      setFormError('We could not load enrollment records, so this submission is not available right now. Retry loading and resubmit.');
      return;
    }

    const email = values.email.trim();
    const alreadyEnrolled = researchers.some(
      (record) => String(record?.email || '').trim().toLowerCase() === email.toLowerCase()
    );
    if (alreadyEnrolled) {
      setFormError('An account with this email is already enrolled. Use the Login link below instead.');
      setFieldErrors({ email: 'This email is already enrolled.' });
      return;
    }

    if (typeof onSubmit !== 'function') return;

    setSubmitting(true);
    try {
      const result = await onSubmit(values);
      const created = result !== null && result !== undefined && result !== false;
      if (!created) {
        setFormError('The server could not create your account. Correct any fields and submit again.');
        return;
      }
      setCreatedEmail(email);
      setValues({ email: '', password: '', confirmPassword: '' });
      setFieldErrors({});
      await refresh();
    } catch (err) {
      const message = err && err.message ? err.message : '';
      setFormError(
        message
          ? `The server could not create your account: ${message}`
          : 'The server could not create your account. Correct any fields and submit again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="sep-section" aria-labelledby="sep-heading">
      <div className="sep-rail" data-content-rail="primary">
        <div className="sep-panel">
          <div className="sep-opener">
            <DrawnMark />
            <span className="sep-label">Enrollment</span>
          </div>

          <h1 className="sep-heading" id="sep-heading">Create your account</h1>
          <p className="sep-lede">
            An account keeps your crawl history and the leads extracted from each crawl. Already enrolled?{' '}
            <a href="/login">Go to Login</a>.
          </p>

          <form className="sep-form" onSubmit={handleSubmit} noValidate aria-label="Enrollment form">
            {formError ? (
              <p className="sep-error" role="alert">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
                  <circle cx="10" cy="10" r="8" />
                  <path d="M10 6v5" />
                  <circle cx="10" cy="14" r="0.6" fill="currentColor" stroke="none" />
                </svg>
                {formError}
                {lookupFailed ? (
                  <>
                    {' '}
                    <button type="button" className="sep-retry" onClick={() => refresh()}>Retry</button>
                  </>
                ) : null}
              </p>
            ) : null}

            {!onSubmit ? (
              <p role="status">Account creation is not available in this preview.</p>
            ) : null}

            {createdEmail ? (
              <p className="sep-success" role="status">
                <span>Account created for {createdEmail}. Redirecting to your crawls — <a href={CRAWLS_PATH}>open Crawls</a>.</span>
              </p>
            ) : null}

            <Field
              id="sep-email"
              label="Email"
              hint="Used to sign in"
              type="email"
              value={values.email}
              error={fieldErrors.email}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => blur('email')}
              autoComplete="email"
              disabled={submitting}
            />
            <Field
              id="sep-password"
              label="Password"
              hint="At least 8 characters"
              type="password"
              value={values.password}
              error={fieldErrors.password}
              onChange={(e) => update('password', e.target.value)}
              onBlur={() => blur('password')}
              autoComplete="new-password"
              disabled={submitting}
            />
            <Field
              id="sep-confirm"
              label="Confirm password"
              type="password"
              value={values.confirmPassword}
              error={fieldErrors.confirmPassword}
              onChange={(e) => update('confirmPassword', e.target.value)}
              onBlur={() => blur('confirmPassword')}
              autoComplete="new-password"
              disabled={submitting}
            />

            <button className="sep-submit" type="submit" disabled={submitting || loading || !onSubmit}>
              <span>{submitting ? 'Creating your account…' : 'Create account'}</span>
              <SubmitArrow />
            </button>
            {submitting ? (
              <div className="sep-progress" role="progressbar" aria-label="Enrollment in progress">
                <span className="sep-progress-fill" />
              </div>
            ) : null}
          </form>

          <p className="sep-login">
            <span>Already have an account?</span>
            <a href="/login">Login</a>
          </p>
        </div>
      </div>
    </section>
  );
}

export default SignUpEnrollmentPanel;